import { Fragment, useEffect, useRef, useState } from "react";

import styles from "@/styles/containers/product-details.module.scss";

import { Button, Checkbox, Textarea } from "@/components";
import { ArrowDown, Check, Edit, Minus, Plus, Times } from "@/components/Icons";

import { Currency } from "@/tools";
import { CatalogHelpers } from "@/tools/CatalogHelpers";
import { OMath } from "@/tools/OMath";
import { number, object } from "yup";

import classNames from "classnames/bind";
import { useLocale, useTranslations } from "next-intl";
import { TextWithLineBreaks } from "@/components/TextWithLineBreaks";
const css = classNames.bind(styles);

const Questions = ({
  localProduct,
  completedQuestions,
  questionSelected,
  setQuestionSelected,
}) => {
  const t = useTranslations();

  const CHOOSE_SIZE = t("chooseSize");
  const CHOOSE_ADDONS = t("additional");

  const hasSizes = localProduct.sizes.length > 1;
  const hasAddons = localProduct.extra_ingredients.length > 0;
  const hasItems = localProduct.items.filter((b) => b.items.length > 0);

  return (
    <div className={styles.questions} id="questions">
      {hasSizes && (
        <div
          className={css({
            question: true,
            active: questionSelected === CHOOSE_SIZE,
            done: completedQuestions.includes(CHOOSE_SIZE),
          })}
          onClick={() => setQuestionSelected(CHOOSE_SIZE)}
        >
          <span className={styles.number}>
            {completedQuestions.includes(CHOOSE_SIZE) ? <Check /> : 1}
          </span>
          <p className={styles.name}>{t("chooseSize")}</p>
          {completedQuestions.includes(CHOOSE_SIZE) && (
            <div className={styles.edit}>
              <Edit />
            </div>
          )}
        </div>
      )}

      {hasAddons > 0 && (
        <div
          className={css({
            question: true,
            active: questionSelected === CHOOSE_ADDONS,
          })}
          onClick={() => setQuestionSelected(CHOOSE_ADDONS)}
        >
          <span className={styles.number}>
            {hasSizes && hasItems
              ? hasItems.length + 2
              : hasItems
              ? hasItems.length + 1
              : hasSizes
              ? 2
              : 1}
          </span>
          <p className={styles.name}>{t("additional")}</p>
          {completedQuestions.includes(CHOOSE_ADDONS) && (
            <div className={styles.edit}>
              <Edit />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Canvas = ({ imageUrl }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      if (img.naturalWidth >= 2500 || img.naturalHeight >= 2500) {
        canvas.width = img.naturalWidth / 5;
        canvas.height = img.naturalHeight / 5;
      }

      context.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  return <canvas ref={canvasRef} />;
};

const prepareProduct = (product) => {
  if ((product.sizes || []).filter((s) => s.selected).length == 0) {
    product.sizes[0].selected = true;
  }

  product.items = product.items.filter((p) => p.items.length > 0);

  product.selected = true;

  return product;
};

export default function Drink({ product, onSave, onClose, isUpdate }) {
  const t = useTranslations();

  const locale = useLocale();

  const [localProduct, setLocalProduct] = useState(prepareProduct(product));
  const [valid, setValid] = useState(false);
  const [questionSelected, setQuestionSelected] = useState(
    localProduct.sizes.length > 1
      ? CHOOSE_SIZE
      : localProduct.extra_ingredients.length > 0
      ? CHOOSE_ADDONS
      : null
  );

  const [completedQuestions, setCompletedQuestions] = useState([]);

  useEffect(() => {
    if (localProduct.sizes.length > 1) {
      setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
    }
  }, [localProduct]);

  useEffect(() => {
    if (questionSelected) {
      const questionEl = document.querySelector(
        `.${styles.question}.${styles.active}`
      );

      if (!questionEl) return;

      questionEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [questionSelected]);

  useEffect(() => {
    validate();
  }, [localProduct]);

  const validate = async () => {
    const productSchema = object().shape({ quantity: number().required() });

    setValid(await productSchema.isValid(localProduct));
  };

  const onProductChange = (data) => {
    setLocalProduct({ ...localProduct, ...data });
    validate();
  };

  const uncheckZeroQuantity = (obj) => {
    if (obj?.hasOwnProperty("selected") && obj?.hasOwnProperty("quantity")) {
      if (obj.quantity === 0) obj.selected = false;
    }

    for (const key in obj) {
      if (typeof obj[key] === "object") {
        uncheckZeroQuantity(obj[key]);
      }
    }
  };

  const save = () => {
    uncheckZeroQuantity(localProduct);

    onSave && onSave(localProduct);
  };

  const calculateItemPrice = (item) => {
    const props = ["items", "sizes", "extra_ingredients", "doughs", "edges"];

    let total =
      Math.round((item.price || 0) * 100) *
      (item.selected !== false && item.quantity > 0 ? 1 : 0);
    for (const key of props) {
      if (item[key] && item[key].length > 0) {
        for (const subItem of item[key]) {
          total += calculateItemPrice(subItem) * 100;
        }
      }
    }

    return +OMath.div(OMath.times(total, item.quantity), 100).toFixed(2);
  };

  const renderPictureSection = () => {
    if (!product.image) {
      return (
        <div
          className={styles.noPictureClose}
          onClick={() => onClose && onClose()}
        >
          <Times color={"#fff"} />
        </div>
      );
    }

    return (
      <div className={styles.picture}>
        <div className={styles.close} onClick={() => onClose && onClose()}>
          <ArrowDown />
        </div>
        <Canvas
          imageUrl={`${process.env.NEXT_PUBLIC_CDN_ASSETS}/${localProduct.image}`}
        />
      </div>
    );
  };

  const renderExtrasSection = () => {
    if ((localProduct.extra_ingredients || []).length == 0) {
      return null;
    }

    return (
      <div className="d-flex flex-col">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("additional")}</p>
        </div>
        <div className={styles.optionItems}>
          {localProduct.extra_ingredients.map((e, eIndex) => {
            const name = CatalogHelpers.getTranslation({
              item: e,
              field: "name",
              locale,
            });

            return (
              <div
                className={styles.optionItem}
                key={eIndex}
                onClick={() => {
                  localProduct.extra_ingredients[eIndex].selected =
                    !localProduct.extra_ingredients[eIndex].selected;
                  onProductChange({
                    extra_ingredients: localProduct.extra_ingredients,
                  });
                }}
              >
                <div className="d-flex flex-col">
                  <p className={styles.optionName}>{name}</p>
                  <div className="d-flex align-center mt-10">
                    <span className={styles.optionPrice}>
                      {`${Currency.formatCurrency(e.price)}`}
                    </span>
                  </div>
                </div>
                <Checkbox
                  value={name}
                  checked={e.selected}
                  key={`extraingredient-${e.id}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderObservationSection = () => {
    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("anyObservations")}</p>
        </div>

        <div className={styles.observationSection}>
          <Textarea
            onChange={(e) => onProductChange({ observation: e.target.value })}
            value={localProduct.observation || ""}
            placeholder={t("exampleRemoveCarrot")}
            className="mt-20"
            max={128}
          />

          <span className={styles.obsLength}>
            {localProduct?.observation?.length || 0}/128
          </span>
        </div>
      </>
    );
  };

  const renderQuantitySection = () => {
    return (
      <div className={styles.quantityContainer}>
        <span
          className={`${styles.minus} ${
            localProduct.quantity <= 1 ? styles.disabled : styles.enabled
          }`}
          onClick={() =>
            localProduct.quantity > 1 &&
            onProductChange({ quantity: --localProduct.quantity })
          }
        >
          <Minus />
        </span>
        <p className={styles.qty}>{localProduct.quantity}</p>
        <span
          className={`${styles.plus} ${styles.enabled}`}
          onClick={() => onProductChange({ quantity: ++localProduct.quantity })}
        >
          <Plus />
        </span>
      </div>
    );
  };

  const renderSizesSection = () => {
    const selectedSize = localProduct.sizes.find((size) => size.selected);
    const isPromotionalPrice = selectedSize.promo_price;

    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("chooseSize")}</p>
          <span className={styles.requiredField}>{t("required")}</span>
        </div>

        <div className={styles.sizes}>
          <span className={styles.sizePrice}>
            {isPromotionalPrice && (
              <>
                <strike className={styles.promoPrice}>
                  {Currency.formatCurrency(selectedSize.old_price)}
                </strike>
                &nbsp;&nbsp;
              </>
            )}
            {Currency.formatCurrency(selectedSize.price)}
          </span>

          {localProduct.sizes.map((size, sizeIndex) => (
            <div
              key={sizeIndex}
              className={`${styles.size} ${
                size.selected ? styles.selected : ""
              }`}
              onClick={() => {
                const hasAddons = localProduct.extra_ingredients.length > 0;

                localProduct.sizes.forEach((s) => (s.selected = false));
                localProduct.sizes[sizeIndex].selected = true;
                onProductChange({ sizes: localProduct.sizes });

                if (!completedQuestions.includes(CHOOSE_SIZE)) {
                  setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
                }

                setQuestionSelected(hasAddons ? CHOOSE_ADDONS : CHOOSE_SIZE);
              }}
            >
              <p className={styles.name}>
                {CatalogHelpers.getTranslation({
                  item: size,
                  field: "name",
                  locale,
                })}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  };

  const isPromotionalPrice = (product) => {
    if (!product?.sizes || product?.sizes?.length <= 0) return false;

    return !!product.sizes.find((size) => size.promo_price === true);
  };

  const renderProductStartPrice = () => {
    const oldPrice = CatalogHelpers.mountOldPrice(localProduct);
    const hasSizes =
      (localProduct.sizes || []).length > 1 ||
      (localProduct.items || []).length > 0;

    return isPromotionalPrice(localProduct) && oldPrice && !hasSizes ? (
      <p className={styles.fromPrice}>
        <strike>{oldPrice}</strike>&nbsp;&nbsp;
        {CatalogHelpers.mountPrice(localProduct)}
      </p>
    ) : (
      <p className={styles.fromPrice}>
        {hasSizes
          ? `${t("startingFrom")} ${CatalogHelpers.mountPrice(localProduct)}`
          : CatalogHelpers.mountPrice(localProduct)}
      </p>
    );
  };

  const elementRef = useRef(null);

  const handleToggleClass = () => {
    if (elementRef.current) {
      elementRef.current.classList.toggle(styles.expand);
    }
  };

  return (
    <>
      <div className={styles.productDetailsContainer}>
        <div className={styles.productDetails}>
          {renderPictureSection()}
          <div className={styles.scrollDetails}>
            <p className={styles.productName}>
              {CatalogHelpers.getTranslation({
                item: product,
                field: "name",
                locale,
              })}
            </p>
            <p
              className={styles.productDescription}
              ref={elementRef}
              onClick={handleToggleClass}
            >
              <TextWithLineBreaks
                text={CatalogHelpers.getTranslation({
                  item: product,
                  field: "description",
                  locale,
                })}
              />
            </p>

            <Questions
              localProduct={localProduct}
              completedQuestions={completedQuestions}
              questionSelected={questionSelected}
              setQuestionSelected={(question) => setQuestionSelected(question)}
            />

            <div className={styles.subtotal}>
              <p>{t("subtotal")}</p>
              <p className={styles.total}>
                {Currency.formatCurrency(calculateItemPrice(localProduct))}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.productContainer}>
          <div className={styles.productChooses}>
            {localProduct.sizes.length > 1 &&
              questionSelected === CHOOSE_SIZE &&
              renderSizesSection()}
            {localProduct.extra_ingredients.length > 0 &&
              questionSelected === CHOOSE_ADDONS &&
              renderExtrasSection()}
            {valid && (
              <div className={styles.footer}>
                {renderQuantitySection()}

                <Button
                  design="primary"
                  className="w-100"
                  disabled={!valid}
                  onClick={() => save()}
                >
                  {isUpdate ? t("update") : t("add")}
                </Button>
              </div>
            )}
            {renderObservationSection()}
          </div>
        </div>
      </div>
    </>
  );
}
